import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import BasicInfo from "../../components/BasicProfile/BasicInfo";
import { ReactComponent as UserIcon } from "../../assets/Icons/user.svg";
import { ReactComponent as UploadIcon } from "../../assets/Icons/upload-cloud.svg";
import AboutMe from "../../components/BasicProfile/AboutMe";
import Contact from "../../components/BasicProfile/Contact";
import OfficeInfo from "../../components/BasicProfile/OfficeInfo";
import AddressCard from "../../components/BasicProfile/AddressCard";
import LinksCard from "../../components/BasicProfile/LinksCard";
import ImageCropperModal from "../../components/ImageCropperModal";
import { useBasicInfo } from "../../contexts/BasicInfo";
import { LOADING, SUCCESS } from "../../constants/common";
import { useAlert } from "../../contexts/Alert";
import { useImageUpload } from "../../hooks/useImageUpload";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { profileImageValidationSchema } from "../../validations/profileValidations";
import { trackClickEvent } from "../../mixpanel";

const ProfileBasic = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { sendAlert } = useAlert();
  const {
    fetchInfo,
    fetchStatus,
    editProfilePhoto,
    downloadProfilePhoto,
    removeProfilePhoto,
    updatePhotoStatus,
    updateInfoState,
    data,
  } = useBasicInfo();

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(profileImageValidationSchema),
    mode: "onChange",
    defaultValues: {
      main_image: data?.main_image || null,
    },
  });

  const formValues = useWatch({ control });

  const imageUploadHook = useImageUpload({
    onUpload: (file) => {
      setValue("main_image", file);
      editProfilePhoto(file);
    },
    onError: (message) => sendAlert(message, "error"),
  });

  const {
    profileImageURL,
    setProfileImageURL,
    cropperOpen,
    setCropperOpen,
    tempImageSrc,
    error: uploadError,
    handleFileInput,
    handleCroppedImage,
    handleSkipCrop,
  } = imageUploadHook;

  // const handleEditImage = async (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   if (profileImageURL) {
  //     try {
  //       // Download the S3 image
  //       const response = await fetch(profileImageURL);
  //       if (!response.ok) {
  //         throw new Error('Failed to download image');
  //       }

  //       const blob = await response.blob();

  //       // Convert blob to File object
  //       const fileName = profileImageURL.split('/').pop() || 'profile-image.jpg';
  //       const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

  //       // Create a fake file input event to trigger the existing flow
  //       const fakeEvent = {
  //         target: {
  //           files: [file],
  //           value: ''
  //         }
  //       };

  //       // Use the existing handleFileInput which will process the file and open cropper
  //       handleFileInput(fakeEvent);

  //     } catch (error) {
  //       sendAlert('Failed to load image for editing', 'error');
  //     }
  //   }
  // };

  const handleEditImage = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (profileImageURL) {
      try {
        // Download the image as File object
        const file = await downloadProfilePhoto(profileImageURL);

        // Set edit mode before calling handleFileInput
        setIsEditMode(true);

        // Create a proper fake event that matches what handleFileInput expects
        const fakeEvent = {
          target: {
            files: [file],
            value: "C:\\fakepath\\" + file.name, // This will be cleared by handleFileInput
          },
          // Add preventDefault and stopPropagation methods
          preventDefault: () => {},
          stopPropagation: () => {},
        };

        // Call handleFileInput with the fake event - this will handle opening the cropper
        handleFileInput(fakeEvent);
        trackClickEvent("Updated Profile Information", {
          component: "Basic Info",
          page: "Edit Profile",
          action: "Upload Image"
        });
      } catch (error) {
        console.error("Failed to edit image:", error);
      }
    }
  };

  useEffect(() => {
    if (formValues.main_image && typeof formValues.main_image === "string") {
      setProfileImageURL(formValues.main_image);
    }
  }, [formValues]);

  useEffect(() => {
    if (data?.main_image) {
      setValue("main_image", data?.main_image);
      setProfileImageURL(data?.main_image);
    }
  }, [data?.main_image]);

  const handleDeleteImage = async () => {
    try {
      await removeProfilePhoto();
      setProfileImageURL(null);
    } catch {
      sendAlert("Something went wrong while removing profile picture", "error");
    }
  };

  // const handleEditImage = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // if (profileImageURL) {
  //   setIsEditMode(true);
  //   setCropperOpen(true);
  // }
  // };

  const handleEditCrop = async (croppedFile) => {
    setValue("main_image", croppedFile);
    editProfilePhoto(croppedFile);
    setProfileImageURL(URL.createObjectURL(croppedFile));
    setIsEditMode(false);
  };

  const handleNewImageCrop = (croppedFile) => {
    handleCroppedImage(croppedFile);
    setIsEditMode(false);
  };

  const handleCropperClose = () => {
    setCropperOpen(false);
    setIsEditMode(false);
  };

  useEffect(() => {
    // fetchInfo();
  }, []);

  useEffect(() => {
    if (updatePhotoStatus === SUCCESS) {
      updateInfoState("updatePhotoStatus", "");
      fetchInfo();
    }
  }, [updatePhotoStatus]);

  return (
    <Paper
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <UserIcon width={24} height={24} />
        <Typography variant="h4" ml={1}>
          Information
        </Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item md={8} xs={12}>
          <BasicInfo />
        </Grid>
        <Grid item md={4} xs={12}>
          <Box
            sx={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "border.main",
              p: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" mb={1.5}>
              Upload an Image
            </Typography>
            {updatePhotoStatus === LOADING || fetchStatus === LOADING ? (
              <Box
                sx={{
                  border: "1px dashed",
                  borderColor: "border.dark",
                  borderRadius: 1.5,
                  height: 158,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "primary.light",
                  },
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    border: "1px dashed",
                    borderColor: "border.dark",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    height: 158,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: profileImageURL
                        ? "transparent"
                        : "primary.light",
                    },
                  }}
                >
                  {profileImageURL ? (
                    <>
                      {/* Clickable Avatar */}
                      <label
                        style={{
                          width: "100%",
                          height: "100%",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                        }}
                      >
                        <Avatar
                          src={profileImageURL}
                          sx={{
                            width: "100%",
                            height: "auto",
                            borderRadius: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          onClick={handleEditImage}
                          sx={{
                            textAlign: "center",
                            position: "absolute",
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.4)",
                            width: "100%",
                            color: "background.paper",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.6)",
                            },
                          }}
                        >
                          Edit
                        </Typography>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileInput}
                          style={{ display: "none" }}
                          name="main_image"
                        />
                      </label>
                      {/* Delete Button */}
                      <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={handleDeleteImage}
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: 5,
                          width: 22,
                          height: 22,
                          backgroundColor: "background.paper",
                          borderRadius: "50%",
                          "&:hover": {
                            backgroundColor: "error.light",
                            color: "error.main",
                          },
                          zIndex: 10,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <label
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Button
                        startIcon={<UploadIcon />}
                        sx={{
                          p: 0,
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        component="span"
                      >
                        Upload
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileInput}
                        style={{ display: "none", overflow: "hidden" }}
                        name="main_image"
                      />
                    </label>
                  )}
                  {(!!errors.main_image || uploadError) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ textAlign: "center", p: 1 }}
                    >
                      {errors.main_image?.message || uploadError}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <AboutMe />
      <Contact />
      <AddressCard />
      <LinksCard />
      <OfficeInfo />
      <ImageCropperModal
        open={cropperOpen}
        onClose={handleCropperClose}
        imageSrc={tempImageSrc || profileImageURL}
        onCrop={isEditMode ? handleEditCrop : handleNewImageCrop}
        onSkip={handleSkipCrop}
      />
    </Paper>
  );
};

export default ProfileBasic;
